import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import type { DrugResolutionNode as DrugResolutionNodeType } from "@/lib/pharmacyFirst/types";
import type {
  ManagementPlanCapture,
  PreferenceCapture,
} from "@/lib/pharmacyFirst/types/audit.types";

export interface DrugResolutionAnswer {
  resolvedDrugId: string;
  rankedEligibleDrugs: string[];
  selectionReason?: string;
}

interface DrugResolutionNodeProps {
  node: DrugResolutionNodeType;
  eligibleDrugIds: string[];
  drugLabels: Record<string, string>;
  drugDescriptions?: Record<string, string>;
  accumulatedFlags?: string[];
  auditFlagLabels?: Record<string, string>;
  managementPlanCaptures?: ManagementPlanCapture[];
  preferenceCaptures?: PreferenceCapture[];
  isDarkMode?: boolean;
  stepNumber?: number;
  reinitiationDrugId?: string | null;
  onAnswer: (answer: DrugResolutionAnswer) => void;
}

function applyRanking(
  eligibleDrugIds: string[],
  rankingOrder: string[]
): string[] {
  const ranked = rankingOrder.filter((id) => eligibleDrugIds.includes(id));
  const unranked = eligibleDrugIds.filter((id) => !rankingOrder.includes(id));
  return [...ranked, ...unranked];
}

export function DrugResolutionNode({
  node,
  eligibleDrugIds,
  drugLabels,
  drugDescriptions,
  accumulatedFlags = [],
  auditFlagLabels,
  managementPlanCaptures = [],
  preferenceCaptures = [],
  isDarkMode = false,
  stepNumber,
  reinitiationDrugId,
  onAnswer,
}: DrugResolutionNodeProps) {
  const rankedEligible = applyRanking(eligibleDrugIds, node.rankingOrder);
  const recommendedDrugId = rankedEligible[0] ?? null;
  const alternatives = rankedEligible.slice(1);
  const displayOrder = [...eligibleDrugIds].sort((a, b) =>
    (drugLabels[a] ?? a).localeCompare(drugLabels[b] ?? b)
  );

  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  const [selectionReason, setSelectionReason] = useState<string>("");

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  if (recommendedDrugId === null) {
    return (
      <div className={cardClass}>
        {stepNumber !== undefined && (
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
            >
              {stepNumber}
            </div>
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Drug selection
            </h3>
          </div>
        )}
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No eligible drugs remain. This node should not have been reached —
            check that the pathway routes to the noDrugsRemaining outcome
            when the eligible set is empty.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const confirmDisabled =
    selectedDrugId === null || selectionReason.trim() === "";

  const handleConfirm = () => {
    if (!selectedDrugId || confirmDisabled) return;
    onAnswer({
      resolvedDrugId: selectedDrugId,
      rankedEligibleDrugs: rankedEligible,
      selectionReason: selectionReason.trim(),
    });
  };

  const handleConfirmRecommended = () => {
    onAnswer({
      resolvedDrugId: recommendedDrugId,
      rankedEligibleDrugs: rankedEligible,
    });
  };

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
          >
            {stepNumber}
          </div>
        )}
        <div>
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.pharmacistOverrideAllowed ? "Select treatment" : "Drug selection"}
          </h3>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {node.pharmacistOverrideAllowed
              ? "Choose based on clinical eligibility and patient preference. Review any caution flags above before selecting."
              : "Select the treatment to supply"}
          </p>
        </div>
      </div>

      {node.displayAccumulatedFlags && accumulatedFlags.length > 0 && (
        <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-amber-700 bg-amber-950/30" : "border-amber-200 bg-amber-50"}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? "text-amber-200" : "text-amber-900"}`}>
            Accumulated caution flags
          </p>
          <ul className="space-y-1.5">
            {accumulatedFlags.map((flagId) => {
              const label = auditFlagLabels?.[flagId];
              return (
                <li key={flagId} className={`text-xs ${isDarkMode ? "text-amber-100" : "text-amber-900"}`}>
                  {label ?? flagId}
                  {label === undefined && (
                    <span className={`ml-1 ${isDarkMode ? "text-amber-400" : "text-amber-700"}`}>
                      (unlabelled flag)
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {node.displayAccumulatedManagementPlans && managementPlanCaptures.length > 0 && (
        <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-blue-700 bg-blue-950/30" : "border-blue-200 bg-blue-50"}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? "text-blue-200" : "text-blue-900"}`}>
            Accumulated management plans
          </p>
          <div className="space-y-2">
            {managementPlanCaptures.map((capture) => (
              <div
                key={`${capture.auditField}-${capture.capturedAt}`}
                className={`rounded-md border px-3 py-2 ${isDarkMode ? "border-blue-800 bg-blue-950/40" : "border-blue-100 bg-white"}`}
              >
                <p className={`text-xs font-medium mb-1 ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}>
                  {capture.displayLabel || capture.auditField || capture.nodeId}
                </p>
                <p className={`text-sm ${isDarkMode ? "text-blue-100" : "text-blue-950"}`}>
                  {capture.planText}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {node.displayAccumulatedPreferences && preferenceCaptures.length > 0 && (
        <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-indigo-700 bg-indigo-950/30" : "border-indigo-200 bg-indigo-50"}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? "text-indigo-200" : "text-indigo-900"}`}>
            Preferences noted
          </p>
          <div className="space-y-2">
            {preferenceCaptures.map((capture) => (
              <div
                key={`${capture.nodeId}-${capture.capturedAt}`}
                className={`rounded-md border px-3 py-2 ${isDarkMode ? "border-indigo-800 bg-indigo-950/40" : "border-indigo-100 bg-white"}`}
              >
                <p className={`text-xs font-medium mb-1 ${isDarkMode ? "text-indigo-300" : "text-indigo-800"}`}>
                  {capture.questionText}
                </p>
                <p className={`text-sm ${isDarkMode ? "text-indigo-100" : "text-indigo-950"}`}>
                  {capture.answerLabel}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {node.pharmacistOverrideAllowed ? (
        <>
          <ul className="space-y-2 mb-4">
            {displayOrder.map((id) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => setSelectedDrugId(id)}
                  className={`w-full text-left border rounded-lg p-4 transition-colors ${
                    selectedDrugId === id
                      ? isDarkMode
                        ? "border-blue-500 bg-blue-950/40 ring-1 ring-blue-500"
                        : "border-blue-400 bg-blue-50 ring-1 ring-blue-400"
                      : isDarkMode
                        ? "border-gray-700 bg-gray-800 hover:border-gray-500"
                        : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                        {drugLabels[id] ?? id}
                      </span>
                      {reinitiationDrugId && id === reinitiationDrugId && (
                        <span
                          className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            isDarkMode ? "bg-blue-900 text-blue-200" : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          Re-initiation drug
                        </span>
                      )}
                      {drugDescriptions?.[id] && (
                        <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          {drugDescriptions[id]}
                        </p>
                      )}
                    </div>
                    <CheckCircle2 className={`h-4 w-4 flex-shrink-0 ${
                      selectedDrugId === id
                        ? isDarkMode ? "text-blue-400" : "text-blue-600"
                        : "text-transparent"
                    }`} />
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {selectedDrugId !== null && (
            <div className="mb-4">
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                Reason for selecting this treatment
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <textarea
                rows={3}
                value={selectionReason}
                onChange={(e) => setSelectionReason(e.target.value)}
                placeholder="e.g. patient preference, contraindication to another option…"
                className={`w-full rounded-md border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500"
                    : "border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-blue-400 focus:border-blue-400"
                }`}
              />
            </div>
          )}

          <Button
            className="w-full"
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            Confirm selection — {drugLabels[selectedDrugId ?? ""] ?? selectedDrugId ?? ""}
          </Button>
        </>
      ) : (
        <>
          <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-emerald-700 bg-emerald-950/40" : "border-emerald-200 bg-emerald-50"}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className={`text-sm font-semibold ${isDarkMode ? "text-emerald-200" : "text-emerald-900"}`}>
                  {drugLabels[recommendedDrugId] ?? recommendedDrugId}
                </p>
                {drugDescriptions?.[recommendedDrugId] && (
                  <p className={`text-xs mt-1 ${isDarkMode ? "text-emerald-300" : "text-emerald-800"}`}>
                    {drugDescriptions[recommendedDrugId]}
                  </p>
                )}
              </div>
            </div>
          </div>

          {alternatives.length > 0 && (
            <div className="mb-4">
              <p className={`text-xs font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Other eligible options
              </p>
              <ul className="space-y-1.5">
                {alternatives.map((id) => (
                  <li
                    key={id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-750" : "border-gray-100 bg-gray-50"}`}
                  >
                    <CheckCircle2 className={`h-3.5 w-3.5 flex-shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                        {drugLabels[id] ?? id}
                      </span>
                      {drugDescriptions?.[id] && (
                        <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                          {drugDescriptions[id]}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button className="w-full" onClick={handleConfirmRecommended}>
            Confirm — proceed with {drugLabels[recommendedDrugId] ?? recommendedDrugId}
          </Button>
        </>
      )}
    </div>
  );
}
